/* eslint-disable no-console */
import React, { useState } from 'react'
import { compose } from 'redux'
import { lpForm } from 'lp-form'
import { Field, propTypes as formPropTypes } from 'redux-form'
import {
  CloudinaryFileInput,
  RemovableThumbnail,
  CheckboxGroup,
} from 'components'
import { ButtonArea, SubmitButton, Input, Select } from 'lp-components'
import { CLOUD_RESIZE_PRESET } from 'config'
import {
  useCommunity,
  formatObjectsToIds,
  parseIdsToObjects,
  serializeOptions,
  getCloudinaryUploadErrorMessage,
  convertCloudinarySrcUrlToFilename,
} from 'utils'
import * as Types from 'types'
import PropTypes from 'prop-types'
import { withProps } from 'recompose'

const propTypes = {
  ...formPropTypes,
  disciplines: PropTypes.arrayOf(Types.discipline),
  demographics: PropTypes.arrayOf(Types.demographic),
}

const PRONOUN_OPTIONS = [
  'He/Him/His',
  'She/Her/Hers',
  'They/Them/Theirs',
  'Other',
]
const YEARS_OF_EXPERIENCE_OPTIONS = [
  { key: '1', value: 1 },
  { key: '2', value: 2 },
  { key: '3', value: 3 },
  { key: '4', value: 4 },
  { key: '5', value: 5 },
  { key: '6', value: 6 },
  { key: '7', value: 7 },
  { key: '8', value: 8 },
  { key: '9', value: 9 },
  { key: '10+', value: 10 },
]

const TA_REGISTER_PRESENCE_HINT = {
  firstName: '^Enter your first name.',
  lastName: '^Enter your last name.',
  email: '^Enter an email address.',
  address: '^Enter your location address.',
  yearsOfExperience: '^Select years of experience.',
  description: '^Enter a short bio.',
  website: '^Enter your website URL.',
  disciplines: '^Please select at least one discipline.',
  profilePicture: '^Add a profile picture.',
}

// Helper function to build constraints based on community config
const buildConstraints = (community) => {
  const constraints = {}
  const fields = community?.teachingArtistRegister?.fields || {}

  Object.keys(fields).forEach((fieldName) => {
    const fieldConfig = fields[fieldName]

    if (fieldConfig.visible) {
      constraints[fieldName] = {
        presence: fieldConfig.required
          ? {
              message:
                TA_REGISTER_PRESENCE_HINT[fieldName] ||
                '^Please complete this field.',
            }
          : false,
      }

      switch (fieldName) {
        case 'email':
          constraints[fieldName].email = {
            message: '^Use an email address like name@example.com.',
          }
          break
        case 'address':
          constraints[fieldName].format = {
            pattern:
              /^\d+\s+[\w\s.,]+,\s*[\w\s]+,\s*[A-Z]{2}\s+\d{5}(-\d{4})?$/,
            message:
              '^Use a full street address with city, state, and ZIP (e.g. 123 Main St, Chicago, IL 60601).',
          }
          break
      }
    }
  })

  return constraints
}

// Helper function to check if field should be visible
const isFieldVisible = (community, fieldName) => {
  const fields = community?.teachingArtistRegister?.fields || {}
  return fields[fieldName]?.visible !== false // default to true if not specified
}

// Helper function to check if field is required
const isFieldRequired = (community, fieldName) => {
  const fields = community?.teachingArtistRegister?.fields || {}
  return fields[fieldName]?.required === true
}

// Helper function to generate field label with asterisk if required
const getFieldLabel = (community, fieldName, baseLabel) => {
  const required = isFieldRequired(community, fieldName)
  return required ? `${baseLabel}*` : baseLabel
}

// Helper function to filter demographics for racial categories only
const filterRacialDemographics = (demographics) => {
  if (!demographics) return []

  // IDs of non-racial demographics to exclude
  const nonRacialIds = [7, 5, 6] // Special Education, Low Income, English Language Learners

  return demographics.filter(
    (demographic) => !nonRacialIds.includes(demographic.id)
  )
}

function RegisterForm({
  change,
  error,
  handleSubmit,
  submitting,
  disciplines,
  demographics,
}) {
  const community = useCommunity()

  const [avatarImage, setAvatarImage] = useState(null)

  return (
    <form onSubmit={handleSubmit} noValidate>
      <h1>Register a Teaching Artist Account</h1>
      <p className="form-instructions">
        Complete the below fields to register your Teaching Artist Account in
        artlook.
      </p>
      {error && (
        <p className="full-error-message" role="alert">
          {error}
        </p>
      )}

      {(isFieldVisible(community, 'firstName') ||
        isFieldVisible(community, 'lastName')) && (
        <div className="grid-container col-2">
          {isFieldVisible(community, 'firstName') && (
            <Field
              name="firstName"
              type="text"
              label={getFieldLabel(community, 'firstName', 'First Name')}
              placeholder="First Name"
              component={Input}
              autoComplete="given-name"
            />
          )}
          {isFieldVisible(community, 'lastName') && (
            <Field
              name="lastName"
              type="text"
              label={getFieldLabel(community, 'lastName', 'Last Name')}
              placeholder="Last Name"
              component={Input}
              autoComplete="family-name"
            />
          )}
        </div>
      )}

      {isFieldVisible(community, 'email') && (
        <Field
          name="email"
          type="email"
          label={getFieldLabel(community, 'email', 'Email')}
          placeholder="Email Address"
          component={Input}
          autoComplete="email"
        />
      )}

      {isFieldVisible(community, 'address') && (
        <div className="label-container">
          <label>
            Location
            {isFieldRequired(community, 'address') ? '*' : ''}
            <br />
            <span className="asterisk">
              Enter the address in the following format: 123 Main St, Chicago,
              IL 60601. Do not include periods.
              <br />
              Your full address will not appear on the map. Your Show Page will
              only show your State and Zip Code.
            </span>
          </label>
          <Field
            name="address"
            type="text"
            // label={getFieldLabel(community, 'address', 'Location')}
            label={false}
            placeholder="123 Main St, Chicago, IL 60601"
            component={Input}
            autoComplete="street-address"
          />
        </div>
      )}

      {isFieldVisible(community, 'yearsOfExperience') && (
        <Field
          name="yearsOfExperience"
          placeholder="Select"
          component={Select}
          options={serializeOptions(YEARS_OF_EXPERIENCE_OPTIONS, {
            key: 'key',
            value: 'value',
          })}
          label={getFieldLabel(
            community,
            'yearsOfExperience',
            'Years of Experience'
          )}
          aria-label="Years of Experience"
          disabled={false}
        />
      )}

      {isFieldVisible(community, 'description') && (
        <Field
          name="description"
          type="text"
          label={getFieldLabel(community, 'description', 'Bio')}
          placeholder="Bio"
          component={Input}
        />
      )}

      {isFieldVisible(community, 'website') && (
        <Field
          name="website"
          type="text"
          label={getFieldLabel(community, 'website', 'Website')}
          placeholder="Website"
          component={Input}
          autoComplete="url"
        />
      )}

      {isFieldVisible(community, 'disciplines') && (
        <div className="label-container">
          <label>
            Do you provide programs in at least 1 or more of these art
            disciplines?
            {isFieldRequired(community, 'disciplines') ? '*' : ''}{' '}
            <span className="asterisk">Select all that apply.</span>
          </label>
          <div className="floating-checkboxes">
            <Field
              name="disciplines"
              label={false}
              component={CheckboxGroup}
              format={formatObjectsToIds('id')}
              parse={parseIdsToObjects('id')}
              options={serializeOptions(disciplines, {
                key: 'displayName',
                value: 'id',
              })}
            />
          </div>
        </div>
      )}

      {isFieldVisible(community, 'profilePicture') && (
        <div className="label-container">
          <label>
            Profile Picture or Logo
            {isFieldRequired(community, 'profilePicture') ? '*' : ''}{' '}
            <span className="asterisk">
              (Accepted formats include png, jpg and jpeg. Your file must be
              less than 10 MB and will be cropped to be a square when uploaded)
            </span>
          </label>
          <Field
            name="profilePic"
            accept=".png,.jpg,.jpeg"
            label={false}
            component={CloudinaryFileInput}
            uploadPath={`/artists/users/profile-pictures/initialValuesId`}
            uploadPreset={CLOUD_RESIZE_PRESET}
            onUploadSuccess={({ secureUrl }) => {
              const filename = convertCloudinarySrcUrlToFilename(secureUrl)
              setAvatarImage({ src: secureUrl, filename })
              console.log(
                'Profile picture successfully uploaded. Make sure to save your changes.'
              )
            }}
            onUploadFailure={(err) => {
              const message = getCloudinaryUploadErrorMessage(err)
              console.log(message)
            }}
            previewComponent={RemovableThumbnail}
            remove={() => {
              change('profilePic', '')
              setAvatarImage(null)
              console.log(
                'Existing profile picture removed. Make sure to save your changes.'
              )
            }}
            image={avatarImage}
            className="col-4"
          />
        </div>
      )}

      {isFieldVisible(community, 'backgroundCheckFile') && (
        <div className="label-container">
          <label>
            Background Check File
            {isFieldRequired(community, 'backgroundCheckFile') ? '*' : ''}{' '}
            <span className="asterisk">
              (Please note: Teaching Artists must submit a current background
              check to be approved for artlook®, please upload your file here.
              If a background check is not provided, you will not be approved
              for artlook®)
            </span>
          </label>
          <Field
            name="backgroundCheckFile"
            label={false}
            component={CloudinaryFileInput}
            uploadPath={`/artists/users/background-check-files/initialValuesId`}
            onUploadSuccess={({ secureUrl }) => {
              console.log(
                secureUrl,
                'Background check file successfully uploaded. Make sure to save your changes.'
              )
            }}
            onUploadFailure={(err) => {
              const message = getCloudinaryUploadErrorMessage(err)
              console.log(message)
            }}
            className="col-4"
          />
        </div>
      )}

      {isFieldVisible(community, 'pronouns') && (
        <Field
          name="pronouns"
          placeholder="Select"
          component={Select}
          options={PRONOUN_OPTIONS}
          label={getFieldLabel(community, 'pronouns', 'Pronouns')}
          aria-label="Pronouns"
          disabled={false}
        />
      )}

      {isFieldVisible(community, 'demographic') && (
        <Field
          name="demographic"
          placeholder="Select"
          component={Select}
          options={serializeOptions(filterRacialDemographics(demographics), {
            key: 'displayName',
            value: 'id',
          })}
          label={getFieldLabel(community, 'demographic', 'Racial Demographics')}
          aria-label="Racial Demographics"
          disabled={false}
        />
      )}

      {isFieldVisible(community, 'idealWage') && (
        <Field
          name="idealWage"
          type="text"
          label={getFieldLabel(community, 'idealWage', 'Ideal Wage Rate')}
          placeholder="Ideal Wage Rate"
          component={Input}
        />
      )}

      <ButtonArea>
        <SubmitButton className="full-width" submitting={submitting}>
          Create Pending Account
        </SubmitButton>
      </ButtonArea>
    </form>
  )
}

RegisterForm.propTypes = propTypes

// Create a component that builds the form with dynamic constraints
const RegisterFormWithConfig = (props) => {
  const community = useCommunity()
  const constraints = buildConstraints(community)

  const FormWithConstraints = compose(
    withProps(() => ({
      beforeSubmit: ({ disciplines, ...values }) => {
        return { ...values, disciplines: formatObjectsToIds('id', disciplines) }
      },
    })),
    lpForm({
      name: 'teaching-artist-portal-register',
      constraints,
      initialValues: {
        persistSession: true,
      },
    })
  )(RegisterForm)

  return <FormWithConstraints {...props} />
}

export default RegisterFormWithConfig
